// CreatorSocialPanel.tsx — réseaux sociaux connectés d'un créateur, dans l'onglet Ressources
//
// Même présentation que les autres panneaux (barre de filtres, bascule cartes / liste, mêmes
// cartes, même liste). Pas de logo en base : la tuile porte le nom du réseau sur sa couleur.
// Données : admin-api AdminResourceItems (kind = social) — jamais les identifiants de connexion.

import React, { useEffect, useMemo, useState } from 'react';

import styles from './ChannelsSection.module.scss';
import extra  from './VideosSection.module.scss';
import { ListTable, TitleCell, WhenCell, Dash } from './DashboardList';
import { Segment, ViewToggle, useView, plural, tintOf } from './dashboardShared';
import { getAdminResourceItems, AdminResourceItem } from '../../../backendServices/adminManagment/api';

const VIEW_KEY = 'adminResources.socialView';

type Sort = 'recent' | 'name';
const SORTS: readonly (readonly [Sort, string])[] = [ [ 'recent', 'Plus récents' ], [ 'name', 'Nom (A → Z)' ] ];
const byDateDesc = (a: AdminResourceItem, b: AdminResourceItem) => (b.date ?? '').localeCompare(a.date ?? '');
const COMPARE: Record<Sort, (a: AdminResourceItem, b: AdminResourceItem) => number> = {
  recent: byDateDesc,
  name  : (a, b) => a.title.localeCompare(b.title, 'fr', { sensitivity: 'base', numeric: true }),
};

// Couleur de marque, à défaut une teinte stable.
const BRAND: Record<string, string> = {
  youtube: '#7F1D1D', facebook: '#1E3A8A', instagram: '#831843', tiktok: '#134E4A', twitch: '#4C1D95', linkedin: '#1E40AF', x: '#1F2937', twitter: '#1F2937',
};
const colorOf = (n: AdminResourceItem) => BRAND[(n.code ?? '').toLowerCase()] ?? tintOf(n.id);

const Pic: React.FC<{ n: AdminResourceItem; mini?: boolean }> = ({ n, mini }) => {
  if (mini) return <div style={{ width: '100%', height: '100%', background: colorOf(n) }} />;
  return (
    <div className={styles.thumb} style={{ background: colorOf(n), opacity: n.active === false ? 0.7 : 1 }}>
      <span className={styles.thumbName}>{n.title}</span>
      <span className={styles.badges} style={{ zIndex: 1, position: 'absolute' }}>
        <span className={`${styles.badge} ${n.active ? styles.badgeOn : styles.badgeOff}`}>{n.active ? 'ACTIF' : 'INACTIF'}</span>
      </span>
    </div>
  );
};

const StateBadge: React.FC<{ n: AdminResourceItem }> = ({ n }) =>
  <span className={`${styles.badge} ${n.active ? styles.badgeOn : styles.badgeOff}`}>{n.active ? 'ACTIF' : 'INACTIF'}</span>;

const CreatorSocialPanel: React.FC<{ adminId: number }> = ({ adminId }) => {

  const [ networks, setNetworks ] = useState<AdminResourceItem[]>([]);
  const [ loading , setLoading  ] = useState(true);
  const [ error   , setError    ] = useState(false);
  const [ state   , setState    ] = useState<'all' | 'on' | 'off'>('all');
  const [ network , setNetwork  ] = useState<string>('all');
  const [ query   , setQuery    ] = useState('');
  const [ sort    , setSort     ] = useState<Sort>('recent');
  const [ view    , setView     ] = useView(VIEW_KEY);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(false);
    getAdminResourceItems(adminId, 'social').then(r => {
      if (cancelled) return;
      if (r.code === 1) setNetworks(r.payload.items); else setError(true);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [adminId]);

  const names = useMemo(() => Array.from(new Set(networks.map(n => n.title))).slice(0, 6), [networks]);

  const matching = useMemo(() => {
    const q = query.trim().toLowerCase();
    return networks.filter(n => (network === 'all' || n.title === network) && (!q || `${n.title} ${n.subtitle}`.toLowerCase().includes(q)));
  }, [networks, network, query]);

  const shown = useMemo(() => matching.filter(n => state === 'all' || (state === 'on') === !!n.active).sort(COMPARE[sort]), [matching, state, sort]);

  const on = matching.filter(n => n.active).length;

  if (loading) return <span>Chargement des réseaux sociaux…</span>;
  if (error)   return <div className={styles.empty}>Chargement impossible.</div>;
  if (networks.length === 0) return <div className={styles.empty}>Aucun réseau social connecté.</div>;

  const filtersOn = query.trim() !== '' || state !== 'all' || network !== 'all';

  return (
    <div>
      <div className={styles.toolbar} style={{ marginBottom: 16 }}>
        <div className={extra.search}>
          <svg className={extra.searchIcon} width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="7" cy="7" r="5"/><path d="M11 11l4 4"/></svg>
          <input className={extra.searchInput} type="text" value={query} placeholder="Réseau, compte…" aria-label="Rechercher un réseau social"
                 onChange={e => setQuery(e.target.value)} />
          {query && <button type="button" className={extra.searchClear} onClick={() => setQuery('')} aria-label="Effacer la recherche">✕</button>}
        </div>
        <Segment label="État" value={state} onChange={setState}
                 choices={[ ['all', `Tous ${matching.length}`], ['on', `Actifs ${on}`], ['off', `Inactifs ${matching.length - on}`] ] as const} />
        {names.length > 1 &&
          <Segment label="Réseau" value={network} onChange={setNetwork}
                   choices={[ ['all', 'Tous les réseaux'] as const, ...names.map(k => [k, k] as const) ]} />}
        {filtersOn && <>
          <span className={extra.count} aria-live="polite"><strong>{plural(shown.length, 'réseau', 'réseaux')}</strong> sur {networks.length}</span>
          <button type="button" className={extra.reset} onClick={() => { setQuery(''); setState('all'); setNetwork('all'); }}>Réinitialiser</button>
        </>}
        <span className={styles.spacer}/>
        <label className={extra.sort}>Trier par
          <select value={sort} onChange={e => setSort(e.target.value as Sort)}>
            {SORTS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>
        <ViewToggle view={view} onChange={setView} />
      </div>

      {shown.length === 0 && <div className={styles.empty}>Aucun réseau ne correspond.</div>}

      {view === 'cards' && shown.length > 0 && (
        <div className={styles.cards}>
          {shown.map(n => (
            <article key={n.id} className={styles.card}>
              <Pic n={n} />
              <div className={styles.cardBody}>
                <span className={styles.cardName} title={n.subtitle || n.title}>{n.subtitle || n.title}</span>
                <div className={styles.chips}>
                  <span>{n.title}</span>
                  {n.date && <span>Connecté le {new Date(n.date).toLocaleDateString('fr-FR')}</span>}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {view === 'list' && shown.length > 0 && (
        <ListTable items={shown} rowKey={n => n.id} dateOf={n => n.date}
                   noun={{ one: 'réseau', many: 'réseaux' }}
                   sort={sort === 'recent' ? null : sort} onSort={id => setSort((id ?? 'recent') as Sort)}
                   columns={[
          { id: 'name', label: 'Réseau', sort: COMPARE.name, cell: n =>
              <TitleCell thumb={<Pic n={n} mini />} title={n.title} titleHint={n.title} sub={n.subtitle} /> },
          { id: 'state', label: 'État', cell: n => <StateBadge n={n} /> },
          { id: 'account', label: 'Compte', cell: n => n.subtitle || <Dash/> },
          { id: 'date', label: 'Connecté', cell: n => <WhenCell iso={n.date} /> },
        ]} />
      )}
    </div>
  );
};

export default CreatorSocialPanel;
